import { Box, Flex, Text } from '@chakra-ui/react'
import { Modal } from '@minetrace/ui'
import { LuChevronLeft as ChevronLeft, LuChevronRight as ChevronRight, LuMapPin as MapPin, LuClock as Clock } from 'react-icons/lu'
import AuthImage from '../../../../components/media/AuthImage'
import { shipmentsService } from '../../../../api/services/shipments.service'
import { themeColors } from '../../../../theme'
import { parseUtcDate } from '../../../../lib/dates'
import type { PhotoWithTime } from '../../../../lib/trackAnalytics'

const colors = themeColors

interface Props {
  shipmentId: string
  photos: PhotoWithTime[]
  index: number | null
  onClose: () => void
  onNavigate: (index: number) => void
}

function formatApprox(iso: string | null): string {
  if (!iso) return 'Hora no disponible'
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  )
}

export default function PhotoLightbox({ shipmentId, photos, index, onClose, onNavigate }: Props) {
  const total = photos.length
  // Mantener el Modal SIEMPRE montado y alternar `open`. Si se desmonta con
  // open=true, Chakra no restaura el scroll-lock del body (quedaba sin scroll).
  const open = index != null && index >= 0 && index < total
  const current = open ? photos[index] : null
  const go = (delta: number) => {
    if (index != null) onNavigate((index + delta + total) % total)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={current?.photo.photoType || 'Foto'}
      subtitle={open ? `${(index ?? 0) + 1} de ${total}` : undefined}
      size="xl"
    >
      {current && (
        <Flex direction="column" gap={4}>
          <Box position="relative" bg="#0b0b0b" borderRadius="lg" overflow="hidden">
            <AuthImage
              src={shipmentsService.getPhotoUrl(shipmentId, current.photo.id)}
              alt={current.photo.photoType}
              minHeight="320px"
              style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }}
            />
            {total > 1 && (
              <>
                <NavButton side="left" onClick={() => go(-1)} />
                <NavButton side="right" onClick={() => go(1)} />
              </>
            )}
          </Box>

          <Flex wrap="wrap" gap={4}>
            <Flex align="center" gap={2}>
              <Clock size={14} color={colors.textMuted} />
              <Text fontSize="sm" color={colors.textSecondary}>
                {formatApprox(current.approxAt)}
                {current.approxAt && (
                  <Text as="span" fontSize="11px" color={colors.textMuted} ml={1}>
                    (aprox.)
                  </Text>
                )}
              </Text>
            </Flex>
            {current.photo.latitude != null && current.photo.longitude != null && (
              <Flex align="center" gap={2}>
                <MapPin size={14} color={colors.textMuted} />
                <Text fontSize="sm" fontFamily="monospace" color={colors.textSecondary}>
                  {current.photo.latitude.toFixed(6)}, {current.photo.longitude.toFixed(6)}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </Modal>
  )
}

function NavButton({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight
  return (
    <Box
      as="button"
      position="absolute"
      top="50%"
      transform="translateY(-50%)"
      {...(side === 'left' ? { left: 3 } : { right: 3 })}
      w="40px"
      h="40px"
      borderRadius="full"
      bg="rgba(0,0,0,0.5)"
      color="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      _hover={{ bg: 'rgba(0,0,0,0.75)' }}
      transition="background 0.15s"
      onClick={onClick}
    >
      <Icon size={22} />
    </Box>
  )
}
